import { useEffect, useRef, useState } from "react";
import { CrownIcon } from "lucide-react";

import winterGreatHall from "@/assets/racetrack/winter-great-hall-fullscreen-v2.webp";
import { Column, Row, Text } from "@/components";
import { StafferPetProfileSprite } from "@/components/staffer-pets/StafferPetProfileSprite";
import { formatting } from "@/lib/formatting";

import type { LeaderboardUser, PetAppearance } from "@/types";

const SCENE_WIDTH_PX = 1536;
const SCENE_HEIGHT_PX = 1024;
const THRONE_SEAT_X_PX = 768;
const THRONE_SEAT_Y_PX = 256;
const THRONE_PET_SIZE_PX = 67;
const MAX_SIDE_CROP_PX = 72;
const PET_FEET_HEIGHT_RATIO = 0.88;
// Bottom step of the throne staircase, in source image pixels.
const STAIRS_BOTTOM_Y_PX = 350;

export const RevenueThroneScene = ({
  leader,
  petByUserId,
  floorTopRatio,
}: {
  leader?: LeaderboardUser;
  petByUserId: Map<number, PetAppearance>;
  // Where the lanes start, as a fraction of the scene height. The image shifts
  // up (cropping ceiling) so the staircase always ends above this line.
  floorTopRatio?: number;
}) => {
  /* React Hooks */
  const sceneRef = useRef<HTMLDivElement>(null);
  const [sceneSize, setSceneSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSceneSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(scene);
    return () => {
      observer.disconnect();
    };
  }, []);

  /* Helpers */
  const pet = leader ? petByUserId.get(leader.userId) : undefined;
  // Fill the viewport; limit side cropping to the stone outside the windows.
  const sceneScale = Math.min(
    Math.max(
      sceneSize.width / SCENE_WIDTH_PX,
      sceneSize.height / SCENE_HEIGHT_PX,
    ),
    sceneSize.width / (SCENE_WIDTH_PX - MAX_SIDE_CROP_PX * 2),
  );
  const imageWidthPx = SCENE_WIDTH_PX * sceneScale;
  const imageHeightPx = SCENE_HEIGHT_PX * sceneScale;
  const imageLeftPx = (sceneSize.width - imageWidthPx) / 2;
  // Wide scenes scale the image by width, which pushes the throne down; lift
  // it so the stairs clear the lanes. Never shifts down (no gap at the top).
  const imageTopPx =
    floorTopRatio === undefined
      ? 0
      : Math.min(
          0,
          sceneSize.height * floorTopRatio - STAIRS_BOTTOM_Y_PX * sceneScale,
        );
  const petSizePx = THRONE_PET_SIZE_PX * sceneScale;
  const petLeftPx = imageLeftPx + THRONE_SEAT_X_PX * sceneScale;
  const petTopPx =
    imageTopPx +
    THRONE_SEAT_Y_PX * sceneScale -
    petSizePx * PET_FEET_HEIGHT_RATIO;

  /* Render */
  const renderOccupant = () => {
    if (!pet || sceneSize.width === 0) return null;

    return (
      <Column
        className="pointer-events-none absolute -translate-x-1/2 drop-shadow-lg"
        style={{ left: petLeftPx, top: petTopPx }}
      >
        <StafferPetProfileSprite
          pet={{ ...pet, isCrowned: true }}
          isOnLeaderboard
          sizePx={petSizePx}
          className="[&>img:last-child]:contrast-125 [&>img:last-child]:grayscale"
        />
      </Column>
    );
  };

  const render = () => {
    return (
      <Column
        ref={sceneRef}
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <img
          src={winterGreatHall}
          alt=""
          className="absolute max-w-none"
          style={{
            top: imageTopPx,
            left: imageLeftPx,
            width: imageWidthPx,
            height: imageHeightPx,
          }}
        />
        {renderOccupant()}
      </Column>
    );
  };

  return render();
};

export const RevenueThroneSummary = ({
  leaders,
  maxRevenue,
}: {
  leaders: LeaderboardUser[];
  maxRevenue: number;
}) => {
  /* Render */
  const render = () => {
    return (
      <Column className="min-w-0 gap-1.5 rounded-xl bg-black/[0.52] px-4 py-3">
        <Row className="items-center gap-2">
          <CrownIcon className="h-3.5 w-3.5 text-slate-300" />
          <Text className="text-sm font-semibold uppercase leading-tight tracking-wider text-slate-200">
            Most revenue
          </Text>
        </Row>
        <Row className="min-w-0 items-center gap-6">
          <Text className="shrink-0 text-4xl font-bold tabular-nums tracking-tight text-white">
            {maxRevenue > 0 ? formatting.currency(maxRevenue) : "—"}
          </Text>
          <Text className="text-lg font-semibold text-white">
            {leaders.map((leader) => leader.firstName).join(" & ") ||
              "The throne awaits"}
          </Text>
        </Row>
      </Column>
    );
  };

  return render();
};
